import { Injectable } from '@angular/core';
import { AppConfig } from './app.config';
import { custom } from 'devextreme/ui/dialog';
import { FeatureFlagItem } from './interfaces/entities/feature-flag';
import { BehaviorSubject } from 'rxjs';
import { currentBrand, UserTenant } from './helpers/tenant-switch';

export type UserData = {
  loginStatus: string;
  email: string | null;
  userRole: string | null;
  userName: string | null;
};
@Injectable({
  providedIn: 'root',
})
export class AppGlobals {
  constructor() {}

  public $headerTitle = new BehaviorSubject<string>('');
  public $currentProjectName = new BehaviorSubject<string | null>(null);
  public $currentSiteName = new BehaviorSubject<string | null>(null);
  public $features = new BehaviorSubject<FeatureFlagItem[]>([]);
  public $showLogs = new BehaviorSubject<boolean>(false);
  public $logItemId = new BehaviorSubject<number | null>(null);
  public $LoggedInUserData = new BehaviorSubject<UserData>({
    loginStatus: 'LoggedOut',
    email: '',
    userRole: '',
    userName: '',
  });

  public static TenantId: string = '';

  public static BaseUrl: string = currentBrand();

  public static $userTenants = new BehaviorSubject<UserTenant[]>([]);

  public static EnvironmentName: string = '';

  public static searchPanelText: any = '';

  public static searchPanelLogsText: any = '';

  public static DeletionID: string = '';

  public static TopNavButtonActive: boolean = false;

  public static logRequestReturnedObject: any = '';

  public static GlobalErrorFeedback: boolean = false;

  public static ShowPopUp: boolean = false;

  public static SelectedPersonData: any = [];

  public static contractPartnerSaveHolder: any = {};

  public static waitingListSaveHolder: any = {};

  public static companyIDForAddAgent: any = '';

  public static companyIDForAddWaitingItem: any = '';

  public static returnedLogData: any = {};

  public static UserToken: string = '';

  public static LoggedInUserData: UserData = {
    loginStatus: 'LoggedOut',
    email: '',
    userRole: '',
    userName: '',
  };

  public static personSaveHolder: any = {};

  public static investorSaveHolder: any = {};

  public static agentSaveHolder: any = {};

  public static investorCompaniesSaveHolder: any = {};

  public static agentCompaniesSaveHolder: any = {};

  public static WarnOnEdit: boolean = false;

  // Getter (not a field): AppConfig.APPVersion is set at startup, after this class is first loaded.
  public static get AppVersion(): string { return AppConfig.APPVersion; }

  public static features: FeatureFlagItem[];
  public static featuresMap: { [Name: string]: boolean } = {};

  public static SetEditWarningStatus(status: boolean): void {
    window.localStorage.setItem('app-warnedit', status ? 'yes' : 'no');
    AppGlobals.WarnOnEdit = status;
  }

  public static GetEditWarningStatus(): boolean {
    return AppGlobals.WarnOnEdit;
  }

  public SetUserDetails(name: string, role: string, email: string): void {
    AppGlobals.LoggedInUserData = {
      loginStatus: 'LoggedIn',
      email: email,
      userRole: role,
      userName: name,
    };
    this.$LoggedInUserData.next(AppGlobals.LoggedInUserData);
  }

  public SetFeatures(data): void {
    AppGlobals.features = [...data];
    AppGlobals.featuresMap = {};
    AppGlobals.features.forEach(feature => (AppGlobals.featuresMap[feature.name] = feature.isEnabled));
    this.$features.next(AppGlobals.features);
  }

  public static UserCan(featureName: string): boolean {
    return !!AppGlobals.featuresMap[featureName];
  }

  public static UserPermissions: any = {
    isAdministrator: false,
    canView: false,
    canEdit: false,
    canComplete: false,
    canValidate: false,
    canDelete: false,
  };

  /**
   * Emits once the server has told us what this user may do. Stays `null` until then, so
   * guards can tell "not an admin" apart from "permissions have not arrived yet" — a deep
   * link into /config resolves before app.component has finished GetUserDetails.
   */
  public static $userPermissions = new BehaviorSubject<any | null>(null);

  public static SetUserPermissions(permissions: any): void {
    AppGlobals.UserPermissions = permissions;
    AppGlobals.$userPermissions.next(permissions);
  }

  public static breadCrumbs: any = [];

  public static setBreadCrumbsFromLocalStorage() {
    let bc = localStorage.getItem('breadCrumbs');
    if (bc != null) {
      let BC = JSON.parse(bc);

      for (let i = 0; i < BC.length; i++) {
        this.breadCrumbs.push(BC[i]);
      }
    }
    this.clearBreadCrumbInLocalStorage();
    /*     this.setBreadCrumbToLocalStorage(this.breadCrumbs); */
  }

  public static async setBreadCrumbToLocalStorage(breadCrumb: any) {
    var breadCrumbToAdd = {
      text: breadCrumb.text,
      href: breadCrumb.href,
      onClick: () => this.goTo(breadCrumb.href),
    };
    this.breadCrumbs.splice(breadCrumb.layer, 3, breadCrumbToAdd);
    localStorage.setItem('breadCrumbs', JSON.stringify(this.breadCrumbs));

    /* if (window.location.href === breadCrumb.href) {
      this.breadCrumbs.length = 0;
    } */
  }

  public static clearBreadCrumbInLocalStorage(): any {
    /*     var breadCrumbToAdd = {
          text: null,
          href: null,
          onClick: null
        };
        this.breadCrumbs.splice(0, 3, breadCrumbToAdd);
        localStorage.setItem('breadCrumbs', JSON.stringify(this.breadCrumbs)); */
    var breadCrumbToAdd = {
      text: null,
      href: null,
      onClick: null,
    };
    this.breadCrumbs.splice(0, 3, breadCrumbToAdd);
    localStorage.removeItem('breadCrumbs');
  }

  public static goTo(link: any) {
    window.location.href = link;
  }

  /**
   * Wipe cached client state on sign-out. MSAL removes only its own `msal.*` entries, so
   * without this the app's own bearer token (AppConfig.AuthTokenLocation) and every cached
   * UI preference outlive the session — on a shared machine the next person at the browser
   * inherits a valid API token.
   *
   * Every logout path must call this before handing off to MSAL's logoutRedirect.
   *
   * AppConfig.ReleaseNoticeKey is deliberately carried across the wipe: it records that this
   * browser has seen the current release, not that someone is signed in. Clearing it would
   * re-show the "new version" popup at every single login.
   */
  public static ClearClientSession(): void {
    AppGlobals.LoggedInUserData = {
      loginStatus: 'LoggedOut',
      email: '',
      userRole: '',
      userName: '',
    };

    AppGlobals.UserPermissions = {
      isAdministrator: false,
      canView: false,
      canEdit: false,
      canComplete: false,
      canValidate: false,
      canDelete: false,
    };
    // null, not the all-false object: the guard waits for "known", and an answer here would
    // bounce an administrator who signs in again without the document being torn down.
    AppGlobals.$userPermissions.next(null);

    document.body.className = 'dx-viewport body-public';

    const lastSeenRelease = localStorage.getItem(AppConfig.ReleaseNoticeKey);
    window.localStorage.clear();
    window.sessionStorage.clear();
    if (lastSeenRelease !== null) {
      localStorage.setItem(AppConfig.ReleaseNoticeKey, lastSeenRelease);
    }
  }

  private focusedPerson: any = [];

  private ui_GlobalModalVisible: boolean = false;
  private ui_GlobalModalContent: string = '';
  private ui_GlobalModalTitle: string = '';
  private ui_GlobalModalIcon: string = '';

  public static LoggedIn: boolean = false;

  public ShowGlobalModal(title: string, message: string, icon: string): void {
    this.ui_GlobalModalVisible = true;
    this.ui_GlobalModalContent = message;
    this.ui_GlobalModalIcon = icon;
    this.ui_GlobalModalTitle = title;
  }

  public HideGlobalModal(): void {
    this.ui_GlobalModalVisible = false;
  }
  public static userDefinedLanguage: string = "German";

  public changeUserDefinedLanguage(udl: string): void {
    AppGlobals.userDefinedLanguage = udl;
  }

  public static GlobalMenuIndex: any = null;

  public SetGlobalMenuIndex(index): void {
    AppGlobals.GlobalMenuIndex = index;
  }

  public static GlobalLoaderVisible: boolean = false;

  public ShowGlobalLoader(): void {
    AppGlobals.GlobalLoaderVisible = true;
  }

  public HideGlobalLoader(): void {
    AppGlobals.GlobalLoaderVisible = false;
  }

  public SetUserLoggedIn() {
    document.body.className = 'dx-viewport body-secure';
    AppGlobals.LoggedIn = true;
  }

  public activateTopNavButton(): void {
    AppGlobals.TopNavButtonActive = true;
  }

  public activatePopUpShow(): void {
    AppGlobals.ShowPopUp = true;
  }

  public deactivatePopUpShow(): void {
    AppGlobals.ShowPopUp = false;
  }

  public changeErrorMessage(a: any): void {
    AppGlobals.GlobalErrorFeedback = a;
  }

  public setDeletionID(a: any): void {
    AppGlobals.DeletionID = a;
  }

  public setCompanyIDForAddAgent(a: any): void {
    AppGlobals.companyIDForAddAgent = a;
  }

  public setCompanyIDForAddWaitingItem(a: any): void {
    AppGlobals.companyIDForAddWaitingItem = a;
  }

  public getReturnedLogData(a: any): void {
    AppGlobals.returnedLogData = a;
  }

  public returnLogRequestForPopUp(a: any): void {
    AppGlobals.logRequestReturnedObject = a;
  }

  public setContractPartnerSaveHolder(a: any): void {
    AppGlobals.contractPartnerSaveHolder = a;
  }

  public setWaitingListSaveHolder(a: any): void {
    AppGlobals.waitingListSaveHolder = a;
  }

  public setPersonSaveHolder(a: any): void {
    AppGlobals.personSaveHolder = a;
  }

  public setInvestorSaveHolder(a: any): void {
    AppGlobals.investorSaveHolder = a;
  }

  public setAgentsSaveHolder(a: any): void {
    AppGlobals.agentSaveHolder = a;
  }

  public setInvestorCompanySaveHolder(a: any): void {
    AppGlobals.investorCompaniesSaveHolder = a;
  }

  public setAgentCompanySaveHolder(a: any): void {
    AppGlobals.agentCompaniesSaveHolder = a;
  }

  public getLoggedinUserData(): any {
    return AppGlobals.LoggedInUserData;
  }

  public static ShowErrorMessage(errordata: any) {
    AppGlobals.GlobalLoaderVisible = false;
    if (errordata.status === 401) {
      AppGlobals.LoggedIn = false;
      return;
    }

    let message = '' + errordata;
    if (errordata.statusText) message = errordata.statusText;

    console.error(errordata);

    let errorAlert = custom({
      messageHtml: $localize`:@@errorCaps:ERROR: ` + message,
      buttons: [
        {
          text: $localize`:@@okCaps:OK`,
          onClick: () => {
            //myAlert.show();
            AppGlobals.GlobalLoaderVisible = false;
          },
        },
      ],
    });
    errorAlert.show();
    /* if (error.status === 401) {
   this.loginFormVisible = true;
   this.logout.emit();
 } */
  }

  public static toastmessage: any = '';
  public static toastType: any = '';
  public static toastVisible: any = '';

  public showToast(message: any, visible: any, type: any) {
    AppGlobals.toastmessage = message;
    AppGlobals.toastType = type;
    AppGlobals.toastVisible = visible;
  }

  ////SAVE SEARCH PANEL TEXT//////
  public setSearchPanelText(a: any): void {
    AppGlobals.searchPanelText = a;
  }

  public setSearchPanelLogsText(a: any): void {
    AppGlobals.searchPanelLogsText = a;
  }
}
